/**
 * Notification preferences service.
 *
 * The `notification_preferences` table is auto-populated on profile
 * INSERT (migration 20260429000004), so reads always succeed for any
 * signed-in user. Updates are RLS-restricted to the row owner.
 *
 * The mobile UI keeps a thin Zustand local cache that mirrors the row
 * for instant render before the network round-trip lands; this service
 * is the only path that hits Supabase.
 */

import { supabase } from '../supabase'
import { ServiceError } from '../errors/service-error'

// ─── Types ──────────────────────────────────────────────────────────────────

export interface NotificationPrefs {
  pushEnabled: boolean
  pushBookingUpdates: boolean
  pushTripReminders: boolean
  pushNewMessages: boolean
  pushHostBookings: boolean
  pushHostCancellations: boolean
  pushHostPayouts: boolean
  pushPromotions: boolean
  emailEnabled: boolean
  smsEnabled: boolean
  updatedAt: string
}

interface NotificationPrefsRow {
  profile_id: string
  push_enabled: boolean
  push_booking_updates: boolean
  push_trip_reminders: boolean
  push_new_messages: boolean
  push_host_bookings: boolean
  push_host_cancellations: boolean
  push_host_payouts: boolean
  push_promotions: boolean
  email_enabled: boolean
  sms_enabled: boolean
  updated_at: string
}

// Mappers map snake_case → camelCase per the codebase convention
// documented in apps/mobile/CLAUDE.md (Architecture · Services).

function mapPrefs(row: NotificationPrefsRow): NotificationPrefs {
  return {
    pushEnabled: row.push_enabled,
    pushBookingUpdates: row.push_booking_updates,
    pushTripReminders: row.push_trip_reminders,
    pushNewMessages: row.push_new_messages,
    pushHostBookings: row.push_host_bookings,
    pushHostCancellations: row.push_host_cancellations,
    pushHostPayouts: row.push_host_payouts,
    pushPromotions: row.push_promotions,
    emailEnabled: row.email_enabled,
    smsEnabled: row.sms_enabled,
    updatedAt: row.updated_at,
  }
}

// camelCase → snake_case for the partial UPDATE payload.
function toSnakePartial(patch: Partial<NotificationPrefs>): Record<string, boolean> {
  const out: Record<string, boolean> = {}
  if (patch.pushEnabled !== undefined) out.push_enabled = patch.pushEnabled
  if (patch.pushBookingUpdates !== undefined) out.push_booking_updates = patch.pushBookingUpdates
  if (patch.pushTripReminders !== undefined) out.push_trip_reminders = patch.pushTripReminders
  if (patch.pushNewMessages !== undefined) out.push_new_messages = patch.pushNewMessages
  if (patch.pushHostBookings !== undefined) out.push_host_bookings = patch.pushHostBookings
  if (patch.pushHostCancellations !== undefined)
    out.push_host_cancellations = patch.pushHostCancellations
  if (patch.pushHostPayouts !== undefined) out.push_host_payouts = patch.pushHostPayouts
  if (patch.pushPromotions !== undefined) out.push_promotions = patch.pushPromotions
  if (patch.emailEnabled !== undefined) out.email_enabled = patch.emailEnabled
  if (patch.smsEnabled !== undefined) out.sms_enabled = patch.smsEnabled
  return out
}

// ─── Service ───────────────────────────────────────────────────────────────

class NotificationPrefsServiceImpl {
  /**
   * Read the prefs row for the given user. Returns null if the trigger
   * hasn't fired yet (race on first signup); the caller falls back to
   * defaults — `fn_should_notify` server-side mirrors this fail-open.
   */
  async getPrefs(profileId: string): Promise<NotificationPrefs | null> {
    const { data, error } = await supabase
      .from('notification_preferences')
      .select(
        'profile_id, push_enabled, push_booking_updates, push_trip_reminders, push_new_messages, push_host_bookings, push_host_cancellations, push_host_payouts, push_promotions, email_enabled, sms_enabled, updated_at',
      )
      .eq('profile_id', profileId)
      .maybeSingle()

    if (error) throw ServiceError.fromSupabaseError(error)
    if (!data) return null
    return mapPrefs(data as NotificationPrefsRow)
  }

  /**
   * Patch a subset of fields. RLS guarantees the update can only touch
   * the caller's own row even if `profileId` is forged client-side.
   */
  async updatePrefs(
    profileId: string,
    patch: Partial<NotificationPrefs>,
  ): Promise<NotificationPrefs> {
    const snake = toSnakePartial(patch)
    if (Object.keys(snake).length === 0) {
      const current = await this.getPrefs(profileId)
      if (!current) throw ServiceError.validation('No preferences row to update')
      return current
    }

    const { data, error } = await supabase
      .from('notification_preferences')
      .update({ ...snake, updated_at: new Date().toISOString() })
      .eq('profile_id', profileId)
      .select(
        'profile_id, push_enabled, push_booking_updates, push_trip_reminders, push_new_messages, push_host_bookings, push_host_cancellations, push_host_payouts, push_promotions, email_enabled, sms_enabled, updated_at',
      )
      .single()

    if (error) throw ServiceError.fromSupabaseError(error)
    return mapPrefs(data as NotificationPrefsRow)
  }
}

export const notificationPrefsService = new NotificationPrefsServiceImpl()

// ─── Defaults (mirror migration 20260429000004) ─────────────────────────────

export const DEFAULT_NOTIFICATION_PREFS: Omit<NotificationPrefs, 'updatedAt'> = {
  pushEnabled: true,
  pushBookingUpdates: true,
  pushTripReminders: true,
  pushNewMessages: true,
  pushHostBookings: true,
  pushHostCancellations: true,
  pushHostPayouts: true,
  pushPromotions: false,
  emailEnabled: true,
  smsEnabled: false,
}
